import { Component, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/core/services/auth.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-home',
    imports: [CommonModule, FormsModule],
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css']
})
export class HomeComponent {

  // Mobile menu control
  mobileMenuOpen = false;
  product = environment.env;

  // Search form state
  search = {
    location: '',
    service: ''
  };

  popularSearches = [
    'Family / Matrimonial Lawyers in Chennai',
    'Commercial Lawyers in Coimbatore',
    'Taxation Lawyers in Bangalore',
    'Corporate Lawyers in Delhi',
    'Education / University / Exam Lawyers in Kerala',
    'Property / Real Estate Lawyers in Mumbai',
    'Medical Negligence Lawyers in Hyderabad'
  ];

  showSpecializationDropdown = false;
  filteredSpecializations: any[] = [];

  // Populated from API — do NOT hardcode
  popularSpecializations: any[] = [];
  selectedSpecialization: any = null;

  isDetectingLocation = true;
  currentLocation = ''; // detected location

  // Auth state (read directly from localStorage)
  isLoggedIn = false;
  profileImg: string = '';
  firstName: string = '';

  ngOnInit() {
    console.log('prod',this.product)
    this.loadSpecializations();

    this.detectUserLocation(); //detect current location

    // Check login state - localStorage key is 'TOKEN' (uppercase)
    const token = localStorage.getItem('TOKEN');
    this.isLoggedIn = !!token;

    if (this.isLoggedIn) {
      // Always read first_name from localStorage (set at login/register time)
      this.firstName = localStorage.getItem('first_name') || '';

      // Call the pic API instead of relying on localStorage which is never populated.
      // This mirrors the pattern used in the settings component.
      // this.auth.get_image().subscribe({
      //   next: (res: any) => {
      //     this.profileImg = res?.data?.imageUrl || '';
      //   },
      //   error: () => {
      //     // Fail silently — avatar initial letter will show as fallback
      //     this.profileImg = '';
      //   }
      // });
    }
  }

  /**
   * Fetches case types from the API and populates specialization dropdown options.
   */
  loadSpecializations() {
    fetch(environment.profapi)
      .then(res => res.json())
      .then((data: any) => {
        const rawList: string[] = data?.data || [];
          const sortedList = rawList.sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      );
        this.popularSpecializations = sortedList?.map((name: string) => ({
          name,
          type: 'SPECIALITY'
        }));
        this.filteredSpecializations = [...this.popularSpecializations];
        console.log(this.filteredSpecializations)
      })
      .catch(err => {
        console.error('Failed to load specializations:', err);
        // Fallback: keep empty — user can still type manually
        this.filteredSpecializations = [];
      });
  }

  /**
   * FIXED: Improved geolocation detection with better error handling and validation
   * 
   * Root Cause Analysis:
   * Browser's navigator.geolocation.getCurrentPosition() was returning Seoul coords
   * because:
   * 1. Browser using enableHighAccuracy: false (allows IP-based geolocation - less accurate)
   * 2. No timeout set (could hang indefinitely)
   * 3. Using cached location (maximumAge not set)
   * 4. No validation of coordinates before reverse geocoding
   * 
   * Improvements Made:
   * - enableHighAccuracy: true (forces GPS when available)
   * - timeout: 10000 ms (prevents hanging)
   * - maximumAge: 0 (always get fresh location)
   * - Added India bounds validation
   * - Added better error logging
   * - Added User-Agent header for OpenStreetMap
   * - Added district priority for India (more granular)
   */
  detectUserLocation() {
    this.isDetectingLocation = true;

    // Check if geolocation is supported
    if (!navigator.geolocation) {
      console.warn('Geolocation not supported by this browser');
      this.isDetectingLocation = false;
      return;
    }

    // FIXED: Geolocation options for better accuracy
    const options = {
      enableHighAccuracy: true,  // Force GPS when available (more accurate than IP)
      timeout: 10000,             // 10 second timeout (prevent hanging)
      maximumAge: 0               // Don't use cached location (always get fresh)
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Log coordinates for debugging
        console.log('Browser Geolocation Result:', {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy + ' meters'
        });

        // FIXED: Validate coordinates are within India bounds
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        if (this.isValidIndianCoordinate(lat, lng)) {
          this.reverseGeocode(lat, lng);
        } else {
          console.warn('⚠️ Detected location is OUTSIDE India bounds:', { lat, lng });
          console.warn('This indicates:');
          console.warn('  - Browser using cached/default location (Seoul?)');
          console.warn('  - VPN or proxy is active');
          console.warn('  - Browser location permissions issue');
          console.warn('  - Device location settings incorrect');
          this.isDetectingLocation = false;
          // Don't prefill - let user manually enter location
        }
      },
      (error) => {
        const errorMessages: { [key: number]: string } = {
          1: 'Permission Denied - User blocked location access',
          2: 'Position Unavailable - Browser cannot determine location',
          3: 'Timeout - Location detection took too long'
        };

        console.error('❌ Geolocation Error:', {
          code: error.code,
          message: errorMessages[error.code] || 'Unknown error'
        });
        
        this.isDetectingLocation = false;
        // Fail silently - let user manually enter location
      },
      options
    );
  }

  /**
   * FIXED: Validate coordinates are within India geographic bounds
   * Kumbakonam is at approximately: 11.1513°N, 79.3878°E
   * Seoul (incorrect) is at approximately: 37.5665°N, 126.9780°E
   */
  private isValidIndianCoordinate(lat: number, lng: number): boolean {
    // India geographic bounds (approximate)
    const MIN_LAT = 8;      // Southernmost: Kanyakumari (~8.1°N)
    const MAX_LAT = 35;     // Northernmost: Siachen (~35°N)
    const MIN_LNG = 68;     // Westernmost: Gujarat (~68°E)
    const MAX_LNG = 97;     // Easternmost: Arunachal Pradesh (~97°E)

    const isValid = lat >= MIN_LAT && lat <= MAX_LAT && lng >= MIN_LNG && lng <= MAX_LNG;
    
    if (!isValid) {
      console.error('🗺️ Coordinate Validation Failed:', {
        detected: { lat, lng },
        expected: { lat: '8-35°N', lng: '68-97°E' },
        status: 'INVALID - Coordinate is outside India'
      });
    }
    
    return isValid;
  }

  /**
   * FIXED: Improved reverse geocoding with better error handling and location priority
   */
  reverseGeocode(lat: number, lng: number) {
    console.log('🔄 Reverse Geocoding:', { latitude: lat, longitude: lng });
    
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`;

    fetch(url, {
      headers: {
        'User-Agent': 'LexiZ-Lawyer-Platform' // OpenStreetMap requires User-Agent
      }
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`API Error: HTTP ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        console.log('✅ Reverse Geocode Response:', data);

        const address = data.address;

        // FIXED: Better location priority for India
        // Prioritize more specific areas first (city > district > town > village)
        this.currentLocation =
          address.city ||           // Most common (e.g., Chennai, Bangalore)
          address.town ||           // Smaller towns
          address.district ||       // FIXED: Added - district level (very useful in India)
          address.village ||        // Rural areas
          address.state ||          // Fallback to state
          '';

        console.log('📍 Detected Location:', this.currentLocation);

        // Prefill input only if user has not typed anything
        if (!this.search.location && this.currentLocation) {
          this.search.location = this.currentLocation;
        }
      })
      .catch(err => {
        console.error('❌ Reverse Geocode Failed:', err.message);
        this.isDetectingLocation = false;
        // Fail silently - let user manually enter location
      })
      .finally(() => {
        this.isDetectingLocation = false;
      });
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  onServiceFocus() {
    // if (!this.search.service) {
    //   this.filteredSpecializations = [...this.popularSpecializations];
    // }
     this.filteredSpecializations = [...this.popularSpecializations];
  this.showSpecializationDropdown = true;  
  }

  onServiceInput(event: any) {
    const searchTerm = event.target.value.toLowerCase();
    this.selectedSpecialization = null;
    if (searchTerm) {
      this.filteredSpecializations = this.popularSpecializations.filter(spec =>
        spec.name.toLowerCase().includes(searchTerm)
      );
    } else {
      this.filteredSpecializations = [...this.popularSpecializations];
    }
    
    this.showSpecializationDropdown = this.filteredSpecializations.length > 0;
  }

  selectSpecialization(specialization: any) {
    this.search.service = specialization.name;
    this.selectedSpecialization = specialization;
    console.log('....Selected specialization:', specialization);
    this.showSpecializationDropdown = false;
  }

  onServiceBlur() {
    // Delay to allow click on dropdown item
    setTimeout(() => {
    this.showSpecializationDropdown = false;

    if (!this.selectedSpecialization) {
      this.search.service = '';
    }

  }, 200);
  }

  scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  gotoVideoConsult(){
    this.router.navigate(['/consumer/videoconsult']);
  }

  gotoLegalVerification(){
    this.router.navigate(['/consumer/legal-verification'])
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.search-input-wrapper')) {
      this.showSpecializationDropdown = false;
    }
  }

  /**
   * Search submission handler
   */
  onSearch() {
    const params = new URLSearchParams();

    // send city
    const city =
      this.search.location?.trim() ||
      this.currentLocation?.trim();

    if (!city) {
      console.warn('City is required');
      return; // safety check
    }
    params.append('city', city);

    //  Send specialization only if present
    // const specialization = this.search.service?.trim();
    // if (specialization) {
    //   params.append('specialization',specialization);
    // }
    // Only allow if selected from dropdown
    let specializationName ='';
    // console.log('Applied city filter:', this.selectedSpecialization);
    if (this.selectedSpecialization) {
      specializationName = this.selectedSpecialization.name
      params.append('specialization', specializationName);
    } 
    //params.append('specialization', (this.selectedSpecialization?.name || this.selectedSpecialization));

    this.auth.lawyerSearch(params).subscribe(
      (res: any) => {
        this.router.navigate(['/consumer/search-results'], {
          state: { lawyers: res, specialization: specializationName, city: city }
        });
      }
    );
  }

  // Popular search tag click
  selectPopular(tag: string) {
    // Split service and location
    const [servicePart, location] = tag.split(' Lawyers in ');

    // Convert to "Divorce Law"
    this.search.service = `${servicePart}`;
    this.search.location = location;
    this.selectedSpecialization = {
    name: this.search.service
  };
    console.log(this.search.service);   // "Divorce Law"
    console.log(this.search.location);  // "Chennai"
    console.log(this.selectedSpecialization)
  }

  clearLocation() {
    this.search.location = '';
  }

  clearService() {
    this.search.service = '';
    this.selectedSpecialization = null; 
    this.filteredSpecializations = [];
    this.showSpecializationDropdown = false;
  }

  // Lawyer registration CTA
  constructor(public router: Router, private auth: AuthService) {}

  goToLawyerRegister() {
    if (this.product === 'dev') {
      window.location.href = 'https://dev2.lauditor.com/registerotp';
    }
    else if (this.product === 'staging') {
      window.location.href = 'https://staging.lauditor.com/registerotp';
    }
    else {
      window.location.href = 'https://app.lexiz.ai/registerotp';
    }
  }

  // Footer copyright year
  currentYear = new Date().getFullYear();

  login(){
    this.router.navigate(['/consumer/login']);
  }
}